import React from "react";
import { useGetAISessionContextResourcesQuery } from "../ai-sessions.service";
import {
  AiSession,
  LearningTrackTopic,
  LearningTrackTopicComponent,
  SessionContextMode,
  TopicContextItem,
  WordBankItem,
} from "../types/ai-session.entity";
import {
  getAISessionContext,
  getTopicContextCustomScenarioIds,
  getTopicContextDisplayLabel,
  getUniqueContextIds,
  normalizeContextText,
} from "../utils";

type Props = {
  aiSession: AiSession & { id: string };
};

const contextTypeBadgeClassByType: Record<SessionContextMode, string> = {
  TOPIC: "bg-purple-100 text-purple-700 border-purple-200",
  LEARNING_TRACK: "bg-emerald-100 text-emerald-700 border-emerald-200",
  WORD_BANK: "bg-cyan-100 text-cyan-700 border-cyan-200",
};

const wordStatusBadgeClassByStatus: Record<WordBankItem["status"], string> = {
  WEAK: "bg-rose-100 text-rose-700 border-rose-200",
  GETTING_IT: "bg-amber-100 text-amber-700 border-amber-200",
  MASTERED: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

export const AISessionContextHistory: React.FC<Props> = ({ aiSession }) => {
  const context = getAISessionContext(aiSession);
  const topicScenarioIds =
    context.sessionMode === "TOPIC" ? getTopicContextCustomScenarioIds(context.items) : [];
  const shouldFetchContextResources =
    (context.sessionMode === "TOPIC" && topicScenarioIds.length > 0) ||
    context.sessionMode === "WORD_BANK";

  const { data: contextResources, isLoading: isContextLoading } =
    useGetAISessionContextResourcesQuery(context, {
      skip: !shouldFetchContextResources,
    });

  const contextItemIds =
    context.sessionMode === "WORD_BANK"
      ? getUniqueContextIds(context.items)
      : [];
  const scenarioById = new Map(
    (contextResources?.scenarios ?? []).map((scenario) => [scenario.id, scenario] as const)
  );

  return (
    <section className="mt-6 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      <div className="p-6 border-b border-gray-100 flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-semibold m-0">Session Context</h2>
        <span
          className={`inline-flex items-center px-2.5 py-1 rounded-full border text-[11px] font-semibold ${contextTypeBadgeClassByType[context.sessionMode]}`}
        >
          {context.sessionMode}
        </span>
      </div>

      <div className="p-6">
        {context.items.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-6 text-center text-slate-500">
            No context items found
          </div>
        ) : (
          <div className="space-y-4">
            {context.sessionMode === "TOPIC" &&
              context.items.map((topicItem: TopicContextItem, index: number) => {
                if (topicItem.type === "TOPIC") {
                  return (
                    <div
                      key={`${topicItem.type}-${topicItem.id}-${index}`}
                      className="rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-slate-50 p-4 shadow-sm"
                    >
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <span className="text-xs text-gray-500 font-mono">{topicItem.id}</span>
                      </div>
                      <div className="text-sm font-semibold text-slate-800">
                        {getTopicContextDisplayLabel(topicItem)}
                      </div>
                    </div>
                  );
                }

                const scenario = scenarioById.get(topicItem.id);

                return (
                  <div
                    key={`${topicItem.type}-${topicItem.id}-${index}`}
                    className="rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-slate-50 p-4 shadow-sm"
                  >
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span className="text-xs text-gray-500 font-mono">{topicItem.id}</span>
                    </div>

                    <div className="text-sm font-semibold text-slate-800">
                      {getTopicContextDisplayLabel(topicItem, scenarioById)}
                    </div>

                    {scenario?.defaultLearnerRole && (
                      <div className="mt-2 text-sm text-slate-600">
                        Learner role: {scenario.defaultLearnerRole}
                      </div>
                    )}

                    {scenario?.defaultAgentRole && (
                      <div className="text-sm text-slate-600">
                        Coach role: {scenario.defaultAgentRole}
                      </div>
                    )}

                    {!scenario && (
                      <div className="mt-2 text-sm text-slate-500">
                        {isContextLoading
                          ? "Loading scenario details..."
                          : "Scenario details were not found."}
                      </div>
                    )}
                  </div>
                );
              })}

            {context.sessionMode === "WORD_BANK" &&
              contextItemIds.map((wordId: string) => {
                const word = contextResources?.words.find((item) => item.id === wordId);

                return (
                  <div
                    key={wordId}
                    className="rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-slate-50 p-4 shadow-sm"
                  >
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span className="text-xs text-gray-500 font-mono">{wordId}</span>
                      {word?.status && (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold ${wordStatusBadgeClassByStatus[word.status]}`}
                        >
                          {word.status}
                        </span>
                      )}
                    </div>

                    <div className="text-sm font-semibold text-slate-800">
                      {word?.word || "Word"}
                    </div>

                    {word?.pronunciation && (
                      <div className="mt-1 text-sm text-slate-500">{word.pronunciation}</div>
                    )}

                    {word?.translation && (
                      <div className="mt-2 text-sm text-slate-600">{word.translation}</div>
                    )}

                    {word?.grammarLabel && (
                      <div className="mt-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-cyan-200 bg-white text-[10px] font-semibold text-cyan-700">
                          {word.grammarLabel}
                        </span>
                      </div>
                    )}

                    {!word && (
                      <div className="mt-2 text-sm text-slate-500">
                        {isContextLoading
                          ? "Loading word details..."
                          : "Word details were not found."}
                      </div>
                    )}
                  </div>
                );
              })}

            {context.sessionMode === "LEARNING_TRACK" &&
              context.items.map((learningTrackTopic: LearningTrackTopic, index: number) => (
                <div
                  key={`learning-track-topic-${index}`}
                  className="rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-slate-50 p-4 shadow-sm"
                >
                  <div className="text-sm font-semibold text-slate-800">
                    {normalizeContextText(learningTrackTopic.topicTitle) ||
                      `Learning Track Topic ${index + 1}`}
                  </div>

                  <div className="mt-3 space-y-2">
                    {(learningTrackTopic.components ?? []).length > 0 ? (
                      learningTrackTopic.components.map(
                        (
                          component: LearningTrackTopicComponent,
                          componentIndex: number
                        ) => (
                          <div
                            key={`learning-track-topic-${index}-component-${componentIndex}`}
                            className="rounded-xl border border-emerald-100 bg-emerald-50/60 px-3 py-2"
                          >
                            <div className="mb-1">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-emerald-200 bg-white text-[10px] font-semibold text-emerald-700">
                                {component.type}
                              </span>
                            </div>
                            <div className="text-sm text-slate-700 whitespace-pre-wrap break-words">
                              {component.content}
                            </div>
                          </div>
                        )
                      )
                    ) : (
                      <div className="text-sm text-gray-500 italic">
                        No learning-track components
                      </div>
                    )}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default AISessionContextHistory;
